import { z } from 'zod';
import { sortParam } from '../sort.js';
import { nullableDate, optionalDate } from '../common.js';
import { PAYMENT_FILTERS } from '../invoices-out/payments.js';
import { TAX_CODES } from '../finance/index.js';
/**
 * Eingangsrechnungen (KON-142, siehe docs/finanzen.md Abschnitt 4.4). Eine Eingangsrechnung ist zuerst ein Entwurf. Kommt sie als E-Rechnung (ZUGFeRD, Factur-X, XRechnung),
 * liest Wirkia sie aus und sperrt die gelesenen Felder. Alles andere wird als Original angehängt und von Hand erfasst. Mit der Freigabe oder Ablehnung ist sie festgeschrieben.
 */
export const PURCHASE_INVOICE_KINDS = ['invoice', 'credit_note', 'partial'];
export const PURCHASE_INVOICE_KIND_LABELS = { invoice: 'Rechnung', credit_note: 'Gutschrift', partial: 'Anzahlungs- oder Teilrechnung' };
export const PURCHASE_INVOICE_STATUSES = ['draft', 'approved', 'rejected'];
export const PURCHASE_INVOICE_STATUS_LABELS = { draft: 'Entwurf', approved: 'Freigegeben', rejected: 'Abgelehnt' };
/** Woher die Angaben stammen: aus einer E-Rechnung gelesen, nach einem Beleg abgetippt oder ganz ohne Beleg erfasst */
export const PURCHASE_INVOICE_ORIGINS = ['einvoice', 'document', 'manual'];
export const PURCHASE_INVOICE_ORIGIN_LABELS = { einvoice: 'E-Rechnung', document: 'Beleg', manual: 'Von Hand' };
export const PURCHASE_DOCUMENT_ROLES = ['original', 'einvoice_xml', 'attachment'];
const TAX_CODE_KEYS = TAX_CODES.map((c) => c.code);
const text = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));
/** Beim Ändern: nicht angegeben bleibt unverändert (undefined), eine leere Eingabe oder null löscht den Wert */
const patchText = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
const cents = (label = 'Betrag') => z.number(`Bitte ${label} angeben`).int('Betrag in Cent').min(-1_000_000_000_00).max(1_000_000_000_00);
export const purchaseSupplierInput = z
    .object({
    name: z.string().trim().min(1, 'Name fehlt').max(200),
    street: text(200),
    postalCode: text(20),
    city: text(100),
    countryCode: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'Zwei Buchstaben, zum Beispiel DE').nullable().optional().transform((v) => v ?? null),
    vatId: text(30),
    taxNumber: text(30),
    email: text(200),
})
    .strict();
export const purchasePaymentInput = z
    .object({
    iban: z.string().trim().toUpperCase().regex(/^[A-Z]{2}[0-9A-Z]{13,32}$/, 'Keine gültige IBAN').nullable().optional().transform((v) => v ?? null),
    bic: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{8}([A-Z0-9]{3})?$/, 'Keine gültige BIC').nullable().optional().transform((v) => v ?? null),
    reference: text(200),
})
    .strict();
export const purchaseLineInput = z
    .object({
    title: z.string().trim().min(1, 'Bezeichnung fehlt').max(500),
    description: text(2000),
    quantityMilli: z.number('Bitte eine Zahl angeben').int('Höchstens drei Nachkommastellen').min(-100_000_000, 'Menge zu groß').max(100_000_000, 'Menge zu groß').refine((v) => v !== 0, 'Menge darf nicht 0 sein'),
    unit: z.string().trim().min(1, 'Einheit fehlt').max(20),
    unitPriceCents: cents('einen Preis'),
    discountBp: z.number().int().min(0, 'Rabatt mindestens 0 %').max(10000, 'Rabatt höchstens 100 %').default(0),
    taxCode: z.enum(TAX_CODE_KEYS, 'Unbekannter Steuerschlüssel'),
})
    .strict();
/** Summe je Steuerschlüssel, Pflicht bei der Erfassung ohne Positionen (Buchhaltung und Vorsteuer brauchen sie) */
export const purchaseGroupInput = z
    .object({
    taxCode: z.enum(TAX_CODE_KEYS, 'Unbekannter Steuerschlüssel'),
    netCents: cents('den Nettobetrag'),
    taxCents: cents('die Steuer'),
})
    .strict();
const lines = z.array(purchaseLineInput).max(500, 'Höchstens 500 Positionen');
const groups = z.array(purchaseGroupInput).max(TAX_CODES.length, 'Zu viele Steuerschlüssel');
const terms = {
    discountTermsBp: z.number().int().min(1, 'Mehr als 0 %').max(10000).nullable().optional(),
    discountTermsDays: z.number().int().min(1, 'Mindestens 1 Tag').max(365).nullable().optional(),
};
const ledgerAccount = z.string().trim().regex(/^[0-9]{1,8}$/, 'Nur Ziffern, höchstens 8 Stellen').nullable().or(z.literal('').transform(() => null)).optional();
/** Die Buchungsangaben bleiben auch bei einer E-Rechnung frei */
const booking = {
    costCenter: patchText(50),
    ledgerAccount,
    orderId: z.uuid('Ungültiger Auftrag').nullable().optional(),
    inputTaxDeductible: z.boolean().optional(),
    note: patchText(2000),
};
/** Neuer Entwurf von Hand. Ein Entwurf aus einer Datei entsteht über den Import. */
export const createPurchaseInvoiceInput = z
    .object({
    kind: z.enum(PURCHASE_INVOICE_KINDS).default('invoice'),
    supplierContactId: z.uuid('Ungültiger Lieferant').nullable().optional(),
    supplier: purchaseSupplierInput.nullable().optional(),
    supplierInvoiceNumber: text(100),
    relatedInvoiceId: z.uuid().nullable().optional(),
    invoiceDate: optionalDate,
    serviceStart: optionalDate,
    serviceEnd: optionalDate,
    dueDate: optionalDate,
    buyerReference: text(100),
    reverseCharge: z.boolean().optional(),
    discountBp: z.number().int().min(0).max(10000).optional(),
    payment: purchasePaymentInput.nullable().optional(),
    lines: lines.optional(),
    groups: groups.optional(),
    ...terms,
    costCenter: text(50),
    ledgerAccount,
    orderId: booking.orderId,
    inputTaxDeductible: booking.inputTaxDeductible,
    note: text(2000),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.discountTermsBp == null) !== (v.discountTermsDays == null))
        ctx.addIssue({ code: 'custom', path: ['discountTermsDays'], message: 'Skonto braucht Prozent und Tage, oder beides leer' });
    if (v.lines && v.lines.length > 0 && v.groups && v.groups.length > 0)
        ctx.addIssue({ code: 'custom', path: ['groups'], message: 'Mit Positionen errechnet Wirkia die Steuer selbst' });
});
export const updatePurchaseInvoiceInput = z
    .object({
    kind: z.enum(PURCHASE_INVOICE_KINDS).optional(),
    supplierContactId: z.uuid('Ungültiger Lieferant').nullable().optional(),
    supplier: purchaseSupplierInput.nullable().optional(),
    supplierInvoiceNumber: patchText(100),
    relatedInvoiceId: z.uuid().nullable().optional(),
    invoiceDate: nullableDate,
    serviceStart: nullableDate,
    serviceEnd: nullableDate,
    dueDate: nullableDate,
    buyerReference: patchText(100),
    reverseCharge: z.boolean().optional(),
    discountBp: z.number().int().min(0).max(10000).optional(),
    payment: purchasePaymentInput.nullable().optional(),
    lines: lines.optional(),
    groups: groups.optional(),
    ...terms,
    ...booking,
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/**
 * Buchungsangaben einer freigegebenen Eingangsrechnung nachtragen: Sachkonto, Kostenstelle, Auftrag und Notiz. Der Beleg selbst bleibt unverändert, die Änderung steht mit alten und neuen Werten
 * im Protokoll. Der Vorsteuerabzug gehört nicht dazu, er ändert die Steuerzahlen.
 */
export const updatePurchaseBookingInput = z
    .object({ costCenter: booking.costCenter, ledgerAccount: booking.ledgerAccount, orderId: booking.orderId, note: booking.note })
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Felder, die bei einer E-Rechnung gesperrt sind: Die Rechnung ist, was im XML steht. Alles andere sind Buchungsangaben. */
export const EINVOICE_LOCKED_FIELDS = [
    'kind', 'supplier', 'supplierInvoiceNumber', 'relatedInvoiceId', 'invoiceDate', 'serviceStart', 'serviceEnd', 'dueDate', 'buyerReference', 'reverseCharge',
    'discountBp', 'payment', 'lines', 'groups', 'discountTermsBp', 'discountTermsDays',
];
export const rejectPurchaseInvoiceInput = z.object({ reason: z.string().trim().min(1, 'Bitte einen Grund angeben').max(1000) }).strict();
export const listPurchaseInvoicesQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    status: z.enum(PURCHASE_INVOICE_STATUSES).optional(),
    kind: z.enum(PURCHASE_INVOICE_KINDS).optional(),
    origin: z.enum(PURCHASE_INVOICE_ORIGINS).optional(),
    supplierId: z.uuid().optional(),
    orderId: z.uuid().optional(),
    payment: z.enum(PAYMENT_FILTERS).optional(),
    /** Nur Belege dieses Jahres (nach Rechnungsdatum, Entwürfe ohne Datum nach dem Tag der Anlage) */
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    sort: sortParam(['supplier', 'number', 'kind', 'status', 'invoiceDate', 'dueDate', 'gross', 'open', 'createdAt'], '-createdAt').optional(),
    q: z.string().trim().max(100).optional(),
});
//# sourceMappingURL=invoices-in.js.map